import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSessionStore } from "@/core/stores/session.store";
import { useChangeMyPassword } from "@/features/auth/api/use-change-my-password";
import type { ChangeMyPasswordRequest } from "@/features/auth/api/dto";
import {
  createChangePasswordSchema,
  type ChangePasswordFormValues,
} from "@/features/auth/schemas/change-password.schema";

const EMPTY_VALUES: ChangePasswordFormValues = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

/**
 * Écran « Mon compte / Sécurité » (`/app/account/security`) — change le mot
 * de passe de l'utilisateur connecté (`POST /Users/me/password`, guide v4.1
 * §6.9). Formulaire 2 champs + confirmation front, `MutationConfirmPreview`
 * (pas d'argent en jeu), redirection `/login` au 204 (toutes les sessions
 * sont fermées côté serveur). Visible pour **tous les rôles authentifiés**.
 */
export function SecurityPage() {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const tAccount = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => createChangePasswordSchema(), [i18n.language]);

  const navigate = useNavigate();
  const changePassword = useChangeMyPassword();
  const pending = changePassword.isPending;
  const [preview, setPreview] = useState<ChangeMyPasswordRequest | null>(null);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) {
        const firstMessage = parsed.error.issues[0]?.message;
        if (firstMessage) notifSilenceError(firstMessage);
        return;
      }
      setPreview({
        currentPassword: parsed.data.currentPassword,
        newPassword: parsed.data.newPassword,
      });
    },
  });

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-muted-foreground" />
        <h1 className="text-2xl font-semibold text-foreground">
          {t("account.security.title")}
        </h1>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">
            {t("account.security.changePassword.title")}
          </CardTitle>
          <CardDescription>
            {t("account.security.changePassword.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="currentPassword">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="account-security-current">
                    {t("account.security.changePassword.currentPassword")}
                  </Label>
                  <Input
                    id="account-security-current"
                    type="password"
                    autoComplete="current-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="newPassword">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="account-security-new">
                    {t("account.security.changePassword.newPassword")}
                  </Label>
                  <Input
                    id="account-security-new"
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("account.security.changePassword.passwordHint")}
                  </p>
                </div>
              )}
            </form.Field>

            <form.Field name="confirmPassword">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="account-security-confirm">
                    {t("account.security.changePassword.confirmPassword")}
                  </Label>
                  <Input
                    id="account-security-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <div className="flex justify-end">
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("account.security.changePassword.submit")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview && { changePasswordRequest: true }}
        title={t("account.security.changePassword.previewTitle")}
        description={t("account.security.changePassword.previewDescription")}
        confirmLabel={t("account.security.changePassword.submit")}
        t={tAccount}
        pending={pending}
        onConfirm={() => {
          if (!preview) return;
          changePassword.mutate(preview, {
            onSuccess: () => {
              notifSilence(t("account.security.changePassword.feedback"));
              setPreview(null);
              // 204 : le serveur a révoqué toutes les sessions. On nettoie
              // la session locale pour forcer une reconnexion propre.
              useSessionStore.getState().clearSession();
              navigate("/login", { replace: true });
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          });
        }}
      />
    </section>
  );
}
